"use strict";

import { createBrowserHistory } from "history";
import { applyMiddleware, combineReducers, compose, createStore } from "redux";
import { createLogger } from "redux-logger";
import { createEpicMiddleware } from "redux-observable";
import { composeWithDevTools } from "@redux-devtools/extension";
import { BehaviorSubject } from "rxjs";
import { switchMap } from "rxjs/operators";

import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage/session";
import epics from "../_epics";
import reducers from "../_reducers";
import { authConstants } from "../_constants";
import { messageMiddleware, loadingSpinnerMiddleware } from "../_middlewares";
import { createReduxHistoryContext } from "redux-first-history";

const persistConfig = {
  key: "root",
  storage,
  stateReconciler: (inboundState, originalState, reducedState) => {
    return inboundState;
  },
  blacklist: [
    "errorMessage",
    "auth",
    "successMessage",
    "loading",
    "beneficiari",
    "accountLists",
    "transactionHistory",
    "creditCardTransactions",
    "creditCardLastStatement",
    "bankList",
    "accountCurrencyCode",
    "accountNumberValidate",
    "schedulePaymentDetailList",
    "transactionDetails",
    "setting",
    "history",
    "secureMailLists",
    "secureEmailDraft",
    "secureMailMessageList",
    "secureEmail",
    "onboarding",
  ],
};

/** Strip previously persisted onboarding slice (may contain password/PIN/PAN). */
const scrubPersistedOnboardingSecrets = () => {
  try {
    const raw = sessionStorage.getItem("persist:root");
    if (!raw) {
      return;
    }
    const parsed = JSON.parse(raw);
    if (parsed && Object.prototype.hasOwnProperty.call(parsed, "onboarding")) {
      delete parsed.onboarding;
      sessionStorage.setItem("persist:root", JSON.stringify(parsed));
    }
  } catch (e) {
    // Ignore corrupt persist blobs; next persist write will overwrite.
  }
};

const history = createBrowserHistory();

const { routerMiddleware, routerReducer } = createReduxHistoryContext({
  history,
});

const configureStore = () => {
  scrubPersistedOnboardingSecrets();

  const rootReducer = combineReducers({
    ...reducers,
    router: routerReducer,
  });

  const reducer = (state, action) => {
    if (action.type === authConstants.AUTH_CLEAR_STORE) {
      storage.removeItem("persist:root");
      state = { router: { ...state.router } };
    }
    return rootReducer(state, action);
  };

  const epicMiddleware = createEpicMiddleware({
    dependencies: { history },
  });

  const loggerMiddleware = createLogger();

  const middlewares = [
    routerMiddleware,
    epicMiddleware,
    messageMiddleware,
    loadingSpinnerMiddleware,
  ];

  const epic$ = new BehaviorSubject(epics);

  const hotReloadingEpic = (...args) =>
    epic$.pipe(switchMap((epic) => epic(...args)));

  let configuredStore = {};

  if (process.env.NODE_ENV === "production") {
    configuredStore = createStore(
      persistReducer(persistConfig, reducer),
      compose(applyMiddleware(...middlewares)),
    );
  } else {
    configuredStore = createStore(
      persistReducer(persistConfig, reducer),
      composeWithDevTools(applyMiddleware(...middlewares, loggerMiddleware)),
    );
  }

  epicMiddleware.run(epics);

  return configuredStore;
};

export { history, configureStore };
